import React from 'react';
import { Spinner, ClampText } from '@/components/ui';
import type {
  WidgetMetadata,
  WidgetContext,
  WidgetHelpers,
} from './ParentWidget';
import { LoadErrorState } from '@/components/ui';

export interface KPIData {
  value: number;
  description: string;
  /** Optional second figure shown under the description (e.g. "3 approvals pending over 48h"). */
  secondary?: { value: number; label: string };
}

export interface KPIBodyProps {
  data?: KPIData;
  metadata: WidgetMetadata;
  context: WidgetContext;
  helpers: WidgetHelpers<KPIData>;
}

const KPIBody: React.FC<KPIBodyProps> = ({
  data,
  metadata,
  helpers,
}) => {
  if (metadata.loading) {
    return (
      <div className="tw-flex tw-items-center tw-pt-2">
        <Spinner animation="border" size="sm" className="tw-mr-2" role="status">
          <span className="tw-sr-only">Loading...</span>
        </Spinner>
        <span className="tw-text-content-muted tw-text-sm">Loading...</span>
      </div>
    );
  }

  if (metadata.error) {
    return (
      // V2-020: a plain message and Retry – never the raw error, never a 0 or "No data".
      <LoadErrorState what="this figure" onRetry={helpers?.refresh} compact />
    );
  }

  if (metadata.isEmpty) {
    return (
      <div>
        <div className="tw-font-display tw-text-[2.25rem] tw-font-bold tw-leading-none tw-text-line">–</div>
        <div className="tw-text-content-muted tw-text-xs tw-mt-1.5 tw-leading-snug tw-line-clamp-2 kpi-description">No data</div>
      </div>
    );
  }

  const value = data?.value ?? 0;
  const description = data?.description ?? '';

  return (
    <div>
      <div className="tw-font-display tw-text-[2.25rem] tw-font-bold tw-leading-none tw-tracking-tight tw-tabular-nums tw-text-ink kpi-value">
        {value.toLocaleString()}
      </div>
      {/* Clamped to two lines; the full text is the tooltip (W-M7). */}
      <ClampText as="div" className="tw-text-content-muted tw-text-xs tw-mt-1.5 tw-leading-snug kpi-description">
        {description}
      </ClampText>
      {data?.secondary && (
        // One line with an ellipsis and the full text as the tooltip, so a narrow tile can't clip it
        // to a bare number with no explanation (W-01).
        <div
          className="tw-text-xs tw-mt-1 tw-leading-snug tw-truncate kpi-secondary"
          title={`${data.secondary.value.toLocaleString()} ${data.secondary.label}`}
        >
          <span className={`tw-font-semibold tw-tabular-nums ${data.secondary.value > 0 ? 'tw-text-danger' : 'tw-text-content'}`}>
            {data.secondary.value.toLocaleString()}
          </span>{' '}
          <span className="tw-text-content-muted">{data.secondary.label}</span>
        </div>
      )}
    </div>
  );
};

export default KPIBody;
